import {
    createContext,
    DetailedHTMLProps,
    ImgHTMLAttributes,
    ReactNode,
    useContext,
    useRef
} from "react";

interface SuspenseImageState {
    load: (url: string | undefined) => void;
    preload: (url: string) => void;
}

interface LoadState {
    promise: Promise<void>;
    finished: boolean;
}

const SuspenseImageContext = createContext<SuspenseImageState>({
    load: () => undefined,
    preload: () => undefined
});

const useSuspenseImage = () => useContext(SuspenseImageContext);

export const SuspenseImageProvider = ({
    children
}: {
    children: ReactNode;
}) => {
    const loaders = useRef<Record<string, LoadState | undefined>>({});

    const preload = (src: string) => {
        if (!loaders.current[src]) {
            const promise = new Promise<void>((resolve, reject) => {
                const image = new Image();
                image.src = src;
                image.onload = () => {
                    const state = loaders.current[src];
                    if (state) {
                        state.finished = true;
                    }
                    resolve();
                };
                image.onerror = () => {
                    loaders.current[src] = undefined;
                    reject(new Error(`Image error ${src}`));
                };
            });
            loaders.current[src] = {
                promise,
                finished: false
            };
        }
    };

    const load = (src: string | undefined) => {
        if (!src) {
            return;
        }

        preload(src);

        const state = loaders.current[src];
        if (state && !state.finished) {
            // eslint-disable-next-line @typescript-eslint/only-throw-error
            throw state.promise;
        }
    };

    return (
        <SuspenseImageContext.Provider value={{ load, preload }}>
            {children}
        </SuspenseImageContext.Provider>
    );
};

export const useImagePreload = (src: string) => {
    const { preload } = useSuspenseImage();
    preload(src);
};

export const SuspenseImage = ({
    src,
    ...props
}: DetailedHTMLProps<
    ImgHTMLAttributes<HTMLImageElement>,
    HTMLImageElement
>) => {
    const { load } = useSuspenseImage();
    load(src);

    return <img src={src} {...props} />;
};
